/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { deserializeFilter as deserializeAttribute } from 'cio/utils/conditions/deserialize/attribute';
import deserializeActionFilter from 'cio/utils/conditions/deserialize/action';
import deserializeEvent from 'cio/utils/conditions/deserialize/event';
import deserializeAttributeCompare from 'cio/utils/conditions/deserialize/attribute-compare';
import FiltersService from 'cio/services/filters';
import { ConditionEditorSubLayout } from 'cio/utils/condition-editor';
import CampaignModel from 'cio/models/campaign';
import CollectionModel from 'cio/models/collection';
import { ConditionDetailsForBoxes, ConditionType } from 'cio/utils/condition-types';
import { DataSourceLabelVariant } from 'cio/utils/data-source-label';
import { ConditionViewerBackgroundVariant } from 'cio/utils/condition-viewer';
import { AddStyleVariant, DropdownOption } from 'cio/components/condition-list';
import { SerializedLegacyFilterType } from 'cio/types/conditions/legacy-filter';

interface ItemArgs {
  variation?: string;
  subLayout?: ConditionEditorSubLayout;
  excludedNewsletterConditionId?: string | number;
  condition: any;
  index?: number;
  isDisabled?: boolean;
  isCancelDisabled?: boolean;
  segments?: Record<string, any>[];
  direction?: string;
  gapSize?: string;
  size?: string;
  allowBlankName?: boolean;
  controlClassNames?: string;
  event?: string;
  campaign?: CampaignModel;
  webhook?: string;
  allowAllVariationsSelection?: boolean;
  allowMessageConditionSpecificLink?: boolean;
  allowComparisonOperator?: boolean;
  allowMessageVariationSelection?: boolean;
  allowMessageWildcardSelection?: boolean;
  allowInParentWildcardSelection?: boolean;
  allowArrayOperators?: boolean;
  allowJsonOperators?: boolean;
  dropdownInitiallyOpened?: ConditionType[];
  autosuggestValueOverrides?: Record<string, string[] | { label: string; value: string }[]>;
  isLoadingAutosuggestValueOverrides?: boolean;
  allowedConditionOperators?: string[];
  showEventNameInEditor?: boolean;
  editContainerClass?: string;
  labelVariant?: DataSourceLabelVariant;
  conditionViewerBackgroundVariant?: ConditionViewerBackgroundVariant;
  allowForeignEvent?: boolean;
  disableForeignEvent?: boolean;
  isEditing?: boolean;
  hiddenInverseOptions?: string[];
  collection?: CollectionModel;
  allowEmptyCancel?: boolean;
  conditionTypes?: ConditionType[];
  objectTypeId?: string | number;
  classNames?: string;
  addStyle?: AddStyleVariant;
  changeTypeOptions?: DropdownOption[];
  allowChangeTypeInline?: boolean;

  setIsEditingAttribute?: (isEditing: boolean) => unknown;
  onApply: (condition: any, index?: number) => unknown;
  onDelete: (index: ItemArgs['index']) => unknown;
  onChangeType?: (type: SerializedLegacyFilterType) => unknown;
}

export default class Item extends Component<ItemArgs> {
  @service filters!: FiltersService;

  @tracked uuid = window.UUID.generate();

  get dropdownInitiallyOpened() {
    return this.args.dropdownInitiallyOpened ?? [];
  }

  get allowBlankName() {
    return this.args.allowBlankName ?? true;
  }

  get showEventNameInEditor() {
    return this.args.showEventNameInEditor ?? true;
  }

  constructor(owner: unknown, args: ItemArgs) {
    super(owner, args);
    this.setConditionModel();
  }

  @tracked private _isEditing = this.args.isEditing ?? false;

  get isEditing() {
    if (this.args.isEditing != null) {
      return this.args.isEditing;
    }

    return this._isEditing;
  }

  @tracked conditionModel: any = null;

  @action
  setConditionModel() {
    let condition = { ...this.args.condition };

    let attributeTypes = [
      'attribute',
      'event_attribute',
      'collection_attribute',
      'device_attribute',
      'webhook_attribute',
      'object_attribute',
      ConditionType.RelationshipAttribute,
    ];
    if (attributeTypes.includes(condition.type)) {
      condition.collectionSchema = this.args.collection?.get('schema');
      condition.event = this.args.event;
      condition.webhook = this.args.webhook;
      this.conditionModel = deserializeAttribute(condition);
      return;
    }

    if (condition.type === 'attribute_compare') {
      condition.collectionSchema = this.args.collection?.get('schema');
      condition.event = this.args.event;
      condition.webhook = this.args.webhook;
      this.conditionModel = deserializeAttributeCompare(condition);
      return;
    }

    if ((condition.type === 'message' && condition?.event?.type === 'event') || condition?.type === 'foreign_event') {
      this.conditionModel = deserializeEvent(condition);
      return;
    }

    if (condition?.type === 'message' && condition?.event?.type !== 'event') {
      let conditionModel = deserializeActionFilter(condition);

      if ((condition?.event?.name as string).includes('output')) {
        conditionModel.type = this.filters.branchMessageTypes[condition?.event?.name] ?? 'email';
      }
      this.conditionModel = {
        condition: conditionModel,
        type: 'message',
      };
      return;
    }

    this.conditionModel = condition;
  }

  get conditionType() {
    let conditionType = this.args.condition?.type;

    if (this.args.condition?.type === 'message' && this.args.condition?.event?.type === 'event') {
      conditionType = 'event';
    }

    if (this.args.condition?.type === 'message' && this.args.condition?.event?.type === 'foreign_event') {
      conditionType = 'foreign_event';
    }

    if (this.args.condition?.type === 'collection_attribute') {
      return 'collection';
    }

    if (this.args.condition?.type === 'device_change') {
      return 'device_attribute';
    }

    return conditionType;
  }

  get blockClass() {
    if (!this.isEditing) {
      return '';
    }

    if (this.args.variation === 'compact') {
      return 'block fly-flex__block';
    }

    return '';
  }

  get isBoxesStyle() {
    return this.args.addStyle === AddStyleVariant.boxes;
  }

  get conditionTypeBoxTitle() {
    let { type } = this.args.condition;
    if (type != null) {
      return ConditionDetailsForBoxes[type as ConditionType]?.title;
    }
    return;
  }

  @action
  setIsEditingAttribute(isEditing: boolean) {
    if (this.args.setIsEditingAttribute != null) {
      this.args.setIsEditingAttribute(isEditing);
    } else {
      this._isEditing = isEditing;
    }
  }

  @action
  apply(condition: any, index: number) {
    this.args.onApply(condition, index);
    this.setIsEditingAttribute(false);
  }

  @action
  delete() {
    this.args.onDelete(this.args.index);
    this.setConditionModel();
  }

  @action
  cancel() {
    this.setIsEditingAttribute(false);
    this.setConditionModel();
  }

  @action
  edit() {
    this.setIsEditingAttribute(true);
  }
}
